import { useClient } from '@loadsmart/vue-feature-flags';
import { DateTime } from 'luxon';

/** @typedef {import('vue').App} VueApp */

/**
 * Checks if a feature flag is enabled outside of Vue components.
 * This function uses the current Vue app's context to determine the status of the feature flag.
 * If the Vue app is not available, it will return `false`.
 *
 * @param {string} flag - The name of the feature flag to check.
 * @returns {boolean} `true` if the feature flag is enabled, otherwise `false`.
 */
export function isEnabled(flag) {
  /** @type {VueApp | undefined} */
  const app = window.app;
  return Boolean(app?.runWithContext(() => useClient()?.isEnabled?.(flag)) ?? false);
}

// Disable animations and transitions for Playwright tests or development environments
export function disableAnimationsAndTransitions() {
  // Check for Playwright environment
  const isPlaywright = window._PLAYWRIGHT_ !== undefined || Boolean(navigator.webdriver);

  if (isPlaywright) {
    // 1. Remove any existing style with the same ID
    const existingStyle = document.getElementById('disable-animations-style');
    if (existingStyle) {
      existingStyle.remove();
    }

    // 2. Create style element with even higher specificity and !important flags
    const style = document.createElement('style');
    style.setAttribute('type', 'text/css');
    style.setAttribute('id', 'disable-animations-style');
    style.textContent = `*, *::before, *::after { transition: none !important; animation: none !important; }`;

    // 3. Inject style at the end of head to override previously loaded styles
    document.head.appendChild(style);

    // 4. Setup mutation observer to ensure our style remains in the DOM
    const observer = new MutationObserver(() => {
      if (!document.getElementById('disable-animations-style')) {
        document.head.appendChild(style.cloneNode(true));
        console.log('Re-injected animation disabling styles');
      }
    });

    // Observe changes to head and body to catch potential style removals
    observer.observe(document.head, { childList: true, subtree: true });

    console.log('Disabled all CSS animations and transitions for testing environment');
  }
}

/**
  The status will be pending if the time elapsed between the asset visit creation and
  the current time is within the observation event match time window defined by the warehouse.

  * @param {string} createDateTime - The ISO date string of the asset visit creation time.
  * @param {number} observationEventMatchTimeWindow - The time window in minutes within which the observation event match is considered pending.
*/
export function isObservationMatchPending(createDateTime, observationEventMatchTimeWindow) {
  if (!createDateTime || !observationEventMatchTimeWindow) {
    return false;
  }

  const assetVisitCreateDateTime = DateTime.fromISO(createDateTime);
  const timeDiff = assetVisitCreateDateTime.diff(DateTime.now(), 'minutes').minutes;
  const timeExpired = Math.abs(timeDiff) > observationEventMatchTimeWindow;

  return !timeExpired;
}
